import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Button,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { supabase } from '../lib/supabaseClient';
import { formatStaffPhone } from './staffPhone';

/**
 * Who gets texted + called when a Kewix "call them now" request sits unclaimed
 * (T-083, docs/process/CALLBACK_ESCALATION_PLAN.md). Rows that match the
 * request's market, day and caller language (blank = any) are alerted lowest "Order" first, one
 * every 20 minutes; if nobody takes it, the answering-service channel is
 * @channel'd. Swap someone out for a vacation here. Admins edit (RLS).
 */

type RotaRow = {
  id: number;
  market: 'quebec' | 'ontario' | null;
  day_of_week: number | null;
  position: number;
  profile_id: string;
  alert: 'sms_and_call' | 'sms_only';
  /** Absent until the T-084 migration runs; null = any caller. */
  caller_language?: 'en' | 'fr' | null;
};

type Person = {
  id: string;
  name: string;
  phone: string | null;
  active: boolean;
};

const DAYS: Record<number, string> = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun (and holidays)',
};
const ANY = '';

export const CallbackRotaCard = () => {
  const notify = useNotify();
  const [rows, setRows] = useState<RotaRow[] | null>(null);
  const [people, setPeople] = useState<Person[]>([]);

  const load = useCallback(async () => {
    const [{ data: rota, error }, { data: staff }] = await Promise.all([
      supabase
        .from('callback_rota')
        // '*' so the card still loads before the caller_language column exists.
        .select('*')
        .order('position')
        .order('day_of_week', { nullsFirst: true }),
      supabase.from('profiles').select('id, name, phone, active').order('name'),
    ]);
    if (error) {
      // Table not there yet (migration pending): show nothing rather than break Team.
      setRows(null);
      return;
    }
    setRows((rota ?? []) as RotaRow[]);
    setPeople(((staff ?? []) as Person[]).filter((p) => p.active !== false));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const update = async (id: number, patch: Partial<RotaRow>) => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('callback_rota')
      .update({
        ...patch,
        updated_at: new Date().toISOString(),
        updated_by: auth?.user?.id ?? null,
      })
      .eq('id', id);
    if (error) notify(`Rota not saved: ${error.message}`, { type: 'error' });
    else notify('Rota saved', { type: 'info' });
    void load();
  };

  const remove = async (id: number) => {
    const { error } = await supabase
      .from('callback_rota')
      .delete()
      .eq('id', id);
    if (error) notify(`Not removed: ${error.message}`, { type: 'error' });
    void load();
  };

  const add = async () => {
    const first = people[0];
    if (!first) return;
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from('callback_rota').insert({
      market: null,
      day_of_week: null,
      position: Math.max(0, ...(rows ?? []).map((r) => r.position)) + 10,
      profile_id: first.id,
      updated_by: auth?.user?.id ?? null,
    });
    if (error) notify(`Not added: ${error.message}`, { type: 'error' });
    void load();
  };

  if (!rows) return null;
  const byId = new Map(people.map((p) => [p.id, p]));

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Callback on-call rota
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          When a family asks Kewix for an arranger to call them now and nobody
          presses <em>I&apos;ll take it</em> within 5 minutes, the people
          matching the request&apos;s market, day and caller language are texted and called,
          lowest order first, one every 20 minutes. If nobody takes it, the
          answering-service channel gets an @channel.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Market</TableCell>
              <TableCell>Day</TableCell>
              <TableCell>Caller speaks</TableCell>
              <TableCell>Person</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Alert</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const person = byId.get(r.profile_id);
              return (
                <TableRow key={r.id}>
                  <TableCell>{r.position}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      value={r.market ?? ANY}
                      displayEmpty
                      onChange={(e) =>
                        void update(r.id, {
                          market: (e.target.value || null) as RotaRow['market'],
                        })
                      }
                    >
                      <MenuItem value={ANY}>Any</MenuItem>
                      <MenuItem value="quebec">Montreal</MenuItem>
                      <MenuItem value="ontario">Toronto</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      value={
                        r.day_of_week == null ? ANY : String(r.day_of_week)
                      }
                      displayEmpty
                      onChange={(e) =>
                        void update(r.id, {
                          day_of_week: e.target.value
                            ? Number(e.target.value)
                            : null,
                        })
                      }
                    >
                      <MenuItem value={ANY}>Any day</MenuItem>
                      {Object.entries(DAYS).map(([d, label]) => (
                        <MenuItem key={d} value={d}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      value={r.caller_language ?? ANY}
                      displayEmpty
                      onChange={(e) =>
                        void update(r.id, {
                          caller_language: (e.target.value ||
                            null) as RotaRow['caller_language'],
                        })
                      }
                    >
                      <MenuItem value={ANY}>Any</MenuItem>
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="fr">French</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      value={person ? r.profile_id : ANY}
                      displayEmpty
                      onChange={(e) =>
                        e.target.value &&
                        void update(r.id, {
                          profile_id: String(e.target.value),
                        })
                      }
                    >
                      {!person && <MenuItem value={ANY}>(inactive)</MenuItem>}
                      {people.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    {person?.phone ? (
                      formatStaffPhone(person.phone)
                    ) : (
                      <Typography variant="body2" color="warning.main">
                        No phone — skipped
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      value={r.alert}
                      onChange={(e) =>
                        void update(r.id, {
                          alert: e.target.value as RotaRow['alert'],
                        })
                      }
                    >
                      <MenuItem value="sms_and_call">Text + call</MenuItem>
                      <MenuItem value="sms_only">Text only</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label="Remove"
                      onClick={() => void remove(r.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Button size="small" sx={{ mt: 1 }} onClick={() => void add()}>
          Add a row
        </Button>
      </CardContent>
    </Card>
  );
};
